import moment from 'moment';
import { Col, Row, Space } from 'antd';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { FC, useEffect, useState } from 'react';
import useJobs from 'hooks/useJobs';
import { JobResponse } from 'store/jobs/job';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import SuitCaseIcon from 'components/icons/SuitCaseIcon';
import Button from 'components/new-ui/base/button/Button';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import AssignJobModal from 'views/manifests/components/AssignJobModal';
import ArchiveJobModal from 'views/manifests/components/ArchiveJobModal';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import Notification from 'components/ui/base/notification/Notification';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import UnarchiveJobModal from 'views/manifests/components/UnarchiveJobModal';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import useSession from 'hooks/useSession';
import { hasPermissions } from 'config/roles/hasPermissions';
import JobsTable from './components/JobsTable';
import JobFiltersModal from './components/JobFiltersModal';
import AppliedJobFilters from './components/AppliedJobFilters';
import { checkIsAccountsAssociate, checkIsFreightAdmin, checkIsFreightForwarder } from 'config/roles/roles';
import { useJobsFilters } from './useJobsFiters';
import useIndividuals from 'hooks/useIndividuals';
import useCustomers from 'hooks/useCustomers';
import useTeams from 'hooks/useTeams';
import { getErrorMessage } from 'utils/getErrorMessage';
import EditJobModal from 'views/jobs/components/edit-job/EditJobModal';

export interface DeletedDeclaration {
    declarationId: string;
    jobId?: string;
}

const Jobs: FC = () => {
    const navigate = useNavigate();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { archiveJob, unarchiveJob } = useJobs();
    const { userInfo } = useSession();
    const { listIndividuals } = useIndividuals();
    const { listCustomers } = useCustomers();
    const { listTeams } = useTeams();

    const {
        data,
        isLoading,
        refetch,
        setArchived,
        archived,
        queryInputVal,
        exactDate,
        tags,
        clearFilter,
        clearAll,
        pagination,
        sortDirection,
        sort,
        filters,
        handleFiltersChange,
        handleTableChange,
        handleSearch,
        handleClearSearch,
        handleExactDateChange,
    } = useJobsFilters((params) => ({ ...params, assignedToManifest: false }));

    const [modal, setModal] = useState<'archive' | 'unarchive' | 'assign' | 'filter' | 'edit' | null>(null);
    const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);

    const canUpdateJobs = hasPermissions(userInfo, 'jobs', 'UPDATE');
    const canCreateJobs = hasPermissions(userInfo, 'jobs', 'CREATE');
    const isFreightAdmin = checkIsFreightAdmin(userInfo);
    const isAccountsManager = checkIsAccountsAssociate(userInfo);
    const isFreightForwarderTenant = checkIsFreightForwarder(userInfo);

    // Team is available only in Freight Forwarder tenant
    // and the team roles see only resources for their team
    const shouldShowTeamFilter = isFreightAdmin || isAccountsManager;

    // Only tenants that have teams can assign resources
    const shouldShowAssigneeFilter = isFreightForwarderTenant;

    const handleCreateJob = () => {
        navigate('/jobs/new');
    };

    const closeModal = () => setModal(null);

    const handleArchiveJob = async () => {
        if (!selectedJob?.id) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No job selected',
            });
            return;
        }
        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            await archiveJob(selectedJob?.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job Archived',
                description: 'Successfully archived job',
            });
            await refetch();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: errorMessage || 'Could not archive job',
            });
        } finally {
            hideGlobalOverlay();
            closeModal();
        }
    };

    const handleUnarchiveJob = async () => {
        if (!selectedJob?.id) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No job selected',
            });
            return;
        }
        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            await unarchiveJob(selectedJob?.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job Unarchived',
                description: 'Successfully Unarchived job',
            });
            await refetch();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: errorMessage || 'Could not unarchive job',
            });
        } finally {
            hideGlobalOverlay();
            closeModal();
        }
    };

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Home',
                path: '/home',
            },
            {
                breadcrumbName: 'Jobs',
                path: '',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    // Refresh the resources in memory
    useEffect(() => {
        listIndividuals({ size: 200 });
        listCustomers({ size: 200 });
        listTeams({ size: 200 });
    }, [listIndividuals, listCustomers, listTeams]);

    return (
        <>
            <StyledPageWrapper>
                <Space style={{ width: '100%' }} direction="vertical" size={24}>
                    <PageHeader
                        title="Jobs"
                        icon={<SuitCaseIcon />}
                        sidebar={
                            <Space size={16}>
                                <ArchiveFilter
                                    value={archived ? 'archived' : 'active'}
                                    onChange={(val) => {
                                        if (val === 'archived') {
                                            setArchived(true);
                                        } else {
                                            setArchived(undefined);
                                        }
                                    }}
                                />
                                {canCreateJobs && (
                                    <Button icon={<PlusOutlined />} onClick={handleCreateJob} type="primary">
                                        New Job
                                    </Button>
                                )}
                            </Space>
                        }
                    />

                    <Row justify="space-between">
                        <Col>
                            <Space size={8}>
                                <SearchBar
                                    inputPlaceholder="Search jobs"
                                    value={queryInputVal}
                                    onSearch={handleSearch}
                                    onClear={handleClearSearch}
                                />
                                <Button onClick={() => setModal('filter')}>Filter</Button>
                            </Space>
                        </Col>
                        <Col>
                            <DatePicker
                                value={exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined}
                                onChange={handleExactDateChange}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <AppliedJobFilters tags={tags} clearFilter={clearFilter} clearAll={clearAll} />
                    </Row>

                    <JobsTable
                        jobs={data?.list ?? []}
                        loading={isLoading}
                        actions={{
                            archive: (job) => {
                                setSelectedJob(job);
                                setModal('archive');
                            },
                            unarchive: (job) => {
                                setSelectedJob(job);
                                setModal('unarchive');
                            },
                            assign: (job) => {
                                setSelectedJob(job);
                                setModal('assign');
                            },
                            edit: (job) => {
                                setSelectedJob(job);
                                setModal('edit');
                            },
                        }}
                        onRowClick={(record: JobResponse) => navigate(`/jobs/${record.id}`)}
                        onChange={handleTableChange}
                        pagination={pagination}
                        currentSortDirection={sortDirection}
                        currentSortParam={sort}
                        optionalColumns={{
                            assignedTo: isFreightForwarderTenant,
                        }}
                    />
                </Space>
            </StyledPageWrapper>

            {canUpdateJobs && selectedJob && (
                <>
                    <AssignJobModal
                        onSuccess={() => {
                            closeModal();
                            refetch();
                        }}
                        open={modal === 'assign'}
                        onClose={closeModal}
                        jobData={{
                            jobId: selectedJob.id,
                            teamId: selectedJob.teamId,
                        }}
                    />
                    <ArchiveJobModal onArchive={handleArchiveJob} open={modal === 'archive'} onClose={closeModal} />
                    <UnarchiveJobModal
                        onUnarchive={handleUnarchiveJob}
                        open={modal === 'unarchive'}
                        onClose={closeModal}
                    />
                    <EditJobModal
                        job={selectedJob}
                        open={modal === 'edit'}
                        onClose={() => {
                            setSelectedJob(null);
                            closeModal();
                        }}
                        onSuccess={() => {
                            refetch();
                        }}
                    />
                </>
            )}

            <JobFiltersModal
                open={modal === 'filter'}
                filters={filters}
                onClose={closeModal}
                onClearAll={clearAll}
                onClearFilter={clearFilter}
                onFiltersChange={handleFiltersChange}
                optionalFilters={{
                    assignedTo: shouldShowAssigneeFilter,
                    team: shouldShowTeamFilter,
                    customer: true,
                }}
            />
        </>
    );
};
export default Jobs;
